/* eslint-disable @typescript-eslint/no-redeclare */
import * as React from "react";

import { cn } from "@/lib/utils";
import { Callout } from "@/components/fluent-callout-compat";
import { fluentIconComponent } from "@/components/fluent-icon-map";

/**
 * Drop-in shim for Fluent UI's `ContextualMenu`. Built on top of `Callout`
 * for positioning. Renders the items as buttons; supports dividers and
 * disabled state. Submenus and section headers are handled minimally —
 * sections use `itemType === 1`, dividers use `itemType === 2`.
 */

export const ContextualMenuItemType = {
  Normal: 0,
  Header: 1,
  Divider: 2,
  Section: 3,
} as const;
export type ContextualMenuItemType =
  (typeof ContextualMenuItemType)[keyof typeof ContextualMenuItemType];

export interface IContextualMenuItem {
  key: string;
  text?: string;
  iconProps?: { iconName?: string; [key: string]: any };
  itemType?: number;
  disabled?: boolean;
  href?: string;
  target?: string;
  onClick?: (
    ev?:
      | React.MouseEvent<HTMLElement>
      | React.KeyboardEvent<HTMLElement>,
    item?: IContextualMenuItem,
  ) => boolean | void;
  data?: any;
  ariaLabel?: string;
  title?: string;
  className?: string;
  /** Children items — rendered as a flat fallback (no submenu navigation). */
  subMenuProps?: { items?: IContextualMenuItem[] };
  /** Drops */
  styles?: any;
  componentRef?: any;
  [key: string]: any;
}

export interface FluentContextualMenuProps {
  items: IContextualMenuItem[];
  target?: any;
  onDismiss?: (ev?: any) => void;
  onItemClick?: (
    ev?: any,
    item?: IContextualMenuItem,
  ) => boolean | void;
  hidden?: boolean;
  directionalHint?: number;
  gapSpace?: number;
  className?: string;
  style?: React.CSSProperties;
  ariaLabel?: string;
  /** Drops */
  styles?: any;
  componentRef?: any;
  theme?: any;
  isBeakVisible?: boolean;
  shouldFocusOnMount?: boolean;
  calloutProps?: any;
}

export const ContextualMenu: React.FC<FluentContextualMenuProps> = (props) => {
  const {
    items,
    target,
    onDismiss,
    onItemClick,
    hidden,
    directionalHint,
    gapSpace,
    className,
    style,
    ariaLabel,
    shouldFocusOnMount,
  } = props;

  if (hidden) return null;

  const handleItemClick = (
    ev: React.MouseEvent<HTMLElement>,
    item: IContextualMenuItem,
  ) => {
    if (item.disabled) return;
    let dismiss = true;
    // Fluent: returning `true` from onClick prevents dismiss.
    const result = item.onClick?.(ev, item);
    if (result === true) dismiss = false;
    const outerResult = onItemClick?.(ev, item);
    if (outerResult === true) dismiss = false;
    if (dismiss) onDismiss?.(ev);
  };

  return (
    <Callout
      target={target}
      onDismiss={onDismiss}
      directionalHint={directionalHint}
      gapSpace={gapSpace ?? 0}
      setInitialFocus={shouldFocusOnMount}
      ariaLabel={ariaLabel}
      role="menu"
      className={cn("min-w-[180px] p-1", className)}
      style={style}
    >
      <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
        {items.map((item) => {
          if (item.itemType === ContextualMenuItemType.Divider) {
            return (
              <li
                key={item.key}
                role="separator"
                className="my-1 h-px bg-border"
              />
            );
          }
          if (item.itemType === ContextualMenuItemType.Header) {
            return (
              <li
                key={item.key}
                className="px-2 pt-1.5 pb-0.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
              >
                {item.text}
              </li>
            );
          }

          const IconComp = fluentIconComponent(item.iconProps?.iconName);
          const inner = (
            <>
              {IconComp && (
                <IconComp size={16} className="shrink-0 opacity-80" />
              )}
              <span className="flex-1 truncate text-left">{item.text}</span>
            </>
          );

          const sharedProps: React.HTMLAttributes<HTMLElement> & {
            disabled?: boolean;
          } = {
            "aria-label": item.ariaLabel,
            title: item.title,
            className: cn(
              "flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none",
              "hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground",
              item.disabled && "pointer-events-none opacity-50",
              item.className,
            ),
          };

          return (
            <li key={item.key} role="none">
              {item.href ? (
                <a
                  {...(sharedProps as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
                  href={item.href}
                  target={item.target}
                  rel={item.target === "_blank" ? "noopener noreferrer" : undefined}
                  role="menuitem"
                  onClick={(e) => handleItemClick(e as any, item)}
                >
                  {inner}
                </a>
              ) : (
                <button
                  type="button"
                  role="menuitem"
                  disabled={item.disabled}
                  {...(sharedProps as React.ButtonHTMLAttributes<HTMLButtonElement>)}
                  onClick={(e) => handleItemClick(e, item)}
                >
                  {inner}
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </Callout>
  );
};
ContextualMenu.displayName = "ContextualMenu";
